import type {
  MailAccountsQuery,
  MailAdminOverviewQuery,
  MailAutorespondersQuery,
  MailboxRootQuery,
  MailCredentialShareProposalsQuery,
  MailCurrentUserQuotaQuery,
  MailFoldersQuery,
  MailMessageBodyQuery,
  MailMessagesQuery,
  MailRulesQuery,
  MailSignatureAssignmentsQuery,
  MailSignaturesQuery,
  MailSyncStatesQuery,
  MailTrustedSendersQuery,
  MailVirtualFoldersQuery,
  PrepareMailCompositionQuery,
} from '@affine/graphql';
import { MailFolder } from '@affine/graphql';

export type MailAccountInfo = MailAccountsQuery['mailAccounts'][number];
export type MailboxScope = 'private' | 'shared';
export type MailCredentialShareProposalInfo =
  MailCredentialShareProposalsQuery['mailCredentialShareProposals'][number];
export type MailMessageInfo = MailMessagesQuery['mailMessages'][number];
export type MailMailboxFolderInfo = MailFoldersQuery['mailFolders'][number];
export type MailRuleInfo = MailRulesQuery['mailRules'][number];
export type MailSignatureInfo = MailSignaturesQuery['mailSignatures'][number];
export type MailSignatureAssignmentInfo =
  MailSignatureAssignmentsQuery['mailSignatureAssignments'][number];
export type MailAutoresponderInfo =
  MailAutorespondersQuery['mailAutoresponders'][number];
export type MailVirtualFolderInfo =
  MailVirtualFoldersQuery['mailVirtualFolders'][number];
export type MailSyncStateInfo = MailSyncStatesQuery['mailSyncStates'][number];
export type MailTrustedSenderInfo =
  MailTrustedSendersQuery['mailTrustedSenders'][number];
export type PreparedMailCompositionInfo =
  PrepareMailCompositionQuery['prepareMailComposition'];
export type MailMessageBodyInfo = MailMessageBodyQuery['mailMessageBody'];
export type MailAttachmentInfo = MailMessageBodyInfo['attachments'][number];
export type MailMailboxRootInfo = MailboxRootQuery['mailboxRoot'];
export type MailAdminOverviewInfo = MailAdminOverviewQuery['mailAdminOverview'];
export type MailUserQuotaInfo =
  MailCurrentUserQuotaQuery['mailCurrentUserQuota'];

export const MAIL_FOLDERS = [
  MailFolder.inbox,
  MailFolder.sent,
  MailFolder.drafts,
  MailFolder.archive,
  MailFolder.trash,
  MailFolder.junk,
] as const;

export type MailFolderId = (typeof MAIL_FOLDERS)[number];

export function formatMailBytes(value: string | number | null | undefined) {
  const bytes =
    typeof value === 'number' ? value : Number.parseInt(value ?? '0', 10);
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let unitIndex = 0;
  let size = bytes;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size >= 10 || unitIndex === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unitIndex]}`;
}

export function formatMailDate(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatMailSenderDisplayName(input: {
  fromName?: string | null;
  fromAddress?: string | null;
}) {
  const fromName = input.fromName?.trim();
  if (fromName) return fromName;

  const fromAddress = input.fromAddress?.trim();
  if (!fromAddress) return '';

  const addressHeaderMatch = fromAddress.match(
    /^(?:"(?<quoted>[^"]+)"|(?<plain>[^<"]+))\s*<[^>]+>$/u
  );
  const headerName = (
    addressHeaderMatch?.groups?.quoted ??
    addressHeaderMatch?.groups?.plain ??
    ''
  ).trim();
  if (headerName && headerName !== fromAddress) return headerName;

  return fromAddress;
}

export function splitMailAddressList(value: string): string[] {
  return value
    .split(/[,\n;]/u)
    .map(item => item.trim())
    .filter(Boolean);
}

export function stripHtmlForMailPreview(value: string | null | undefined) {
  if (!value) return '';
  return value
    .replace(/<style[\s\S]*?<\/style>/giu, ' ')
    .replace(/<script[\s\S]*?<\/script>/giu, ' ')
    .replace(/<[^>]+>/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim();
}
